import { use, useEffect } from 'react';

import { StatsigClientEventCallback, useStatsigClient } from '@statsig/react-bindings';

import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { snowplowEvent } from 'Utilities/analytics';
import { SNOWPLOW_EXPERIMENT_ENTRY_CONTEXT } from 'Utilities/analytics/snowplowGlobalContext';
import { createStatSigUser } from 'Utilities/analytics/statsig/statsig';

import { getOptimizelyUserId } from '../../../../../client/index.helper';

export const useStatSig = () => {
  const { userState: user } = use(UserContext);
  const { vehicleLookupState: vehicle } = use(VehicleLookupContext);
  const { client } = useStatsigClient();

  const vehicleId = vehicle?.enquiry?.dpEnquiryId ?? user?.vehicles?.at(0)?.enquiry?.dpEnquiryId;

  // When switching enquiries, update the user object with the latest DP Enquiry ID
  useEffect(() => {
    const deviceCookieId = getOptimizelyUserId();
    const statSigUser = createStatSigUser(deviceCookieId, user?.id, vehicleId);
    client.updateUserAsync(statSigUser).catch((error) => {
      window?.Sentry?.captureException?.(new Error(error), {
        extra: {
          enquiryId: vehicleId ?? null,
          sellerId: user?.id ?? null,
        },
        tags: { fetch: 'useStatSig.updateUserAsync' },
      });
    });

    SNOWPLOW_EXPERIMENT_ENTRY_CONTEXT({
      deviceCookieId,
      enquiryId: vehicleId ?? null,
      sellerId: user?.id ?? null,
    });
  }, [vehicleId, user?.id, client]);

  useEffect(() => {
    const onLogEvent: StatsigClientEventCallback<'log_event_called'> = ({ event }) => {
      snowplowEvent({
        data: {
          event_name: event.eventName,
          metadata: event.metadata ? JSON.stringify(event.metadata) : null,
          value: event.value ?? null,
        },
        schema: 'statsig_event',
        version: '1-0-0',
      });
    };

    const onDecisionEvent: StatsigClientEventCallback<'experiment_evaluation'> = ({ experiment }) => {
      if (!experiment.idType || !experiment.groupName) {
        return;
      }

      snowplowEvent({
        data: {
          decision: experiment.groupName,
          flag_name: experiment.name,
          id_type: experiment.idType,
          rule_name: experiment.ruleID,
        },
        schema: 'statsig_decision',
        version: '1-0-1',
      });
    };

    client.on('log_event_called', onLogEvent);
    client.on('experiment_evaluation', onDecisionEvent);

    return () => {
      client.off('log_event_called', onLogEvent);
      client.off('experiment_evaluation', onDecisionEvent);
    };
  }, [client]);
};
